import { useCallback, useMemo } from 'react';
import InvestmentMemoDropzone from 'views/InvestmentMemo/InvestmentMemoDropzone/InvestmentMemoDropzone';
import { ITemporaryUploadedFile } from 'views/ProFormaUpload/types';
import useToast from 'hooks/useToast';
import { useInvestmentMemoContext } from 'views/InvestmentMemo/InvestmentMemoContext';
import { AISuggestedDocument, DealOverviewData } from '../../../types';
import Specs from './Specs/Specs';
import UnitMixRentRoll from './UnitMixRentRoll/UnitMixRentRoll';

export interface DealOverviewFormProps {
    data: DealOverviewData;
    onChange: (data: Partial<DealOverviewData>) => void;
}

const DealOverviewForm = ({ data, onChange }: DealOverviewFormProps) => {
    const { showError } = useToast();
    const {
        documentSuggestions,
        isLoadingDocumentSuggestions,
    } = useInvestmentMemoContext();

    const handleSiteStackingPlanUploaded = useCallback((file: ITemporaryUploadedFile) => {
        try {
            onChange({
                siteStackingPlanFiles: {
                    uploaded: [file], // Only one uploaded file allowed
                    suggested: [], // Clear suggested when uploading
                },
            });
        } catch (err) {
            console.error('Failed to handle uploaded site stacking plan', err);
            showError('Failed to upload site stacking plan');
        }
    }, [onChange, showError]);

    const handleSiteStackingPlanDeleted = useCallback((file: ITemporaryUploadedFile) => {
        onChange({
            siteStackingPlanFiles: {
                uploaded: data.siteStackingPlanFiles.uploaded.filter(f => f.id !== file.id),
                suggested: data.siteStackingPlanFiles.suggested,
            },
        });
    }, [onChange, data.siteStackingPlanFiles]);

    const siteStackingPlanFilesSelectedIds = useMemo(() => new Set(data.siteStackingPlanFiles.suggested.map(f => f.id)),
        [data.siteStackingPlanFiles.suggested]);

    return (
        <div className="px-3 pb-3">
            <InvestmentMemoDropzone
                label="Site or stacking plan (select one)"
                multiple={false}
                onUploaded={handleSiteStackingPlanUploaded}
                onDeleted={handleSiteStackingPlanDeleted}
                filesFromData={data.siteStackingPlanFiles.uploaded}
                documentLibrarySuggestions={{
                    suggestions: documentSuggestions?.dealOverview || [],
                    isLoadingSuggestions: isLoadingDocumentSuggestions,
                    selectedIds: siteStackingPlanFilesSelectedIds,
                    onToggle: (doc: AISuggestedDocument, isSelected: boolean) => {
                        if (isSelected && data.siteStackingPlanFiles.uploaded.length > 0) {
                            data.siteStackingPlanFiles.uploaded.forEach(handleSiteStackingPlanDeleted);
                        }

                        onChange({
                            siteStackingPlanFiles: {
                                uploaded: [], // Clear uploaded when selecting suggested
                                suggested: !isSelected
                                    ? data.siteStackingPlanFiles.suggested.filter(d => d.id !== doc.id)
                                    : [doc], // Only allow one suggested file to be selected
                            },
                        });
                    },
                }}
            />

            <Specs
                specs={data.specs}
                onChange={specsData => onChange({ specs: { ...data.specs, ...specsData } })}
            />

            <UnitMixRentRoll
                unitMixRentRoll={data.unitMixRentRoll}
                onChange={unitMixData => {
                    if (data.unitMixRentRoll) {
                        onChange({ unitMixRentRoll: { ...data.unitMixRentRoll, ...unitMixData } });
                    }
                }}
            />
        </div>
    );
};

export default DealOverviewForm;
